/**
 * useQuoteSubmission - Presentation Hook
 *
 * Orchestrates quote submission workflow
 * Uses Application Service for multi-step submission
 * Validates using domain validators before submission
 *
 * @module useQuoteSubmission
 */

import { useCallback } from "react";
import { useQuoteResponseWorkflow } from "../../application/services/QuoteResponseApplicationService";
import { QuoteErrorApplicationService } from "../../application/services/QuoteErrorApplicationService";
import { validateQuoteResponse } from "../../domain/validators/quoteResponseValidator";
import {
  calculateQuoteAmounts,
  type QuoteItem,
  type QuoteVatMode,
} from "@semo/shared-utils";
import { transformUiItemToApiItem } from "@semo/shared-utils";
import { QuoteErrorType } from "../../../../lib/error-types";
import type {
  CreateQuoteResponseVersionDto,
  QuoteItemDto,
  QuoteResponseItemDto,
} from "@semo/api-contracts";
import type { QuoteFormFile } from "./useQuoteForm";
import { toast } from "sonner";
import { describeAutoRegisteredItems } from "../../domain/catalogAutoRegistration";

/**
 * Custom hook for quote submission orchestration
 * @param quoteRequestId - ID of the quote request to respond to
 * @returns Submission function and loading states
 */
export const useQuoteSubmission = (quoteRequestId: string) => {
  const { executeWorkflow, isLoading, isUploading, isSubmitting } =
    useQuoteResponseWorkflow(quoteRequestId);

  /**
   * Submit quote with validation
   * @throws Error if validation fails or submission fails
   */
  const handleSubmit = useCallback(
    async (
      items: QuoteItem[],
      notes: string,
      files: QuoteFormFile[],
      // 기본값을 두지 않는다 — 호출부가 빠뜨리면 조용히 EXCLUSIVE 로 저장돼
      // 부가세 포함으로 입력한 견적이 별도로 뒤바뀐다(실제 사고).
      vatMode: QuoteVatMode,
    ): Promise<void> => {
      // Step 1: Validate using domain validator
      const validation = validateQuoteResponse({ items, notes, vatMode });

      if (!validation.isValid) {
        const firstError = Object.values(validation.errors)[0];

        // Show error toast for validation failure
        QuoteErrorApplicationService.getInstance().showError({
          type: QuoteErrorType.VALIDATION_ERROR,
          message: firstError ?? "입력 정보가 올바르지 않습니다.",
          action: "입력 값을 확인해주세요.",
        });

        throw new Error(firstError);
      }

      // Step 2: Calculate totals using domain calculators
      // 화면(useQuoteCalculations)과 같은 정본을 통과시켜야 표시값과 전송값이 일치한다.
      const { vatAmount: taxAmount, totalAmount } = calculateQuoteAmounts(
        items,
        vatMode,
      );

      // Step 3: Convert to DTO
      const dtoItems: QuoteResponseItemDto[] = items.map((item) => {
        const transformed = transformUiItemToApiItem({
          ...item,
          name: item.itemName,
          unit: item.unit ?? "개", // Sync original unit
          amount: item.quantity * item.unitPrice,
        });

        // Add specific fields needed for vendor quote DTO
        // ✅ unitPrice MUST be taken from original UI "item", not "transformed"
        const apiItem: QuoteResponseItemDto = {
          ...transformed,
          unitPrice: item.unitPrice,
          updateCatalog: item.updateCatalog,
        };
        return apiItem;
      });

      const quoteData: CreateQuoteResponseVersionDto = {
        totalAmount,
        taxAmount,
        // 단가를 어느 축으로 입력했는지 함께 남긴다 — 이게 없으면 재편집·문서에서
        // 같은 품목 단가를 공급가액으로 잘못 읽는다.
        taxType: vatMode,
        items: dtoItems,
        description: notes || undefined,
      };

      // Step 4: Execute workflow (upload + submit) via Application Service
      const fileObjects = files
        .map((f) => f.file)
        .filter((f): f is File => !!f);
      await executeWorkflow(quoteData, fileObjects);

      // Step 5: 처음 견적한 카탈로그 품목은 서버가 내 상품(판매중)으로 등록한다 — 알려 준다(V-11).
      const autoRegistered = describeAutoRegisteredItems(items);
      if (autoRegistered) {
        toast.info(autoRegistered.title, {
          description: autoRegistered.description,
          duration: 10000,
        });
      }
    },
    [executeWorkflow],
  );

  return {
    handleSubmit,
    isSubmitting: isLoading,
    isUploading,
    isCreatingQuote: isSubmitting,
  };
};
